import Notifications from '@components/notifications/notifications.component'
import { AlfredService, LogService, Logo, MySquid, User, defaultUser, onConfigChange, onUserChange, LanguageDropdown } from '@hub/shared'
import onCountNotificationsChange from '@observables/count-notifications.observable'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { SubscriptionLike } from 'rxjs'
import { UserMenu } from './components/user-menu/user-menu-default.component'
import './header-default.component.scoped.scss'
import { useDeviceType } from '@src/hooks/useDeviceType'
import { MainMenu } from '@src/components/main-menu/main-menu.component'
import { useMediaQuery } from '@src/hooks/useMediaQuery'

interface Props extends React.HTMLAttributes<HTMLDivElement> {}

export const Header = ({ className = '' }: Props) => {
  const onCountNotificationsSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onConfigChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onHistorySubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [configs, setConfig] = useState<MySquid | null>(null)
  const notificationsButton = useRef(null)
  const [user, setUser] = useState<User | null>(null)
  const [openNotifications, setOpenNotifications] = useState<boolean>(false)
  const [countNotifications, setCountNotifications] = useState<number>(0)
  const alfredService = useMemo(() => new AlfredService(), [])
  const logService = useMemo(() => new LogService(), [])
  logService.init('HeaderDefault Componente initialized')
  const location = useLocation()
  const [loadingNotifications, setLoadingNotification] = useState<boolean>(false)
  const [openMobileMenu, setOpenMobileMenu] = useState(false)
  const [isNotificationsHovered, setIsNotificationsHovered] = useState(false)
  const [isUserHelpHovered, setIsUserHelpHovered] = useState(false)
  const isMobile = useDeviceType()
  const isSmallScreen = useMediaQuery('(max-width: 991px)')

  const hoverNotificationEvents = isMobile
    ? {}
    : {
        onMouseEnter: () => {
          setIsNotificationsHovered(true)
        },
        onMouseLeave: () => {
          setIsNotificationsHovered(false)
        },
      }

  const hoverHelpEvents = isMobile
    ? {}
    : {
        onMouseEnter: () => {
          setIsUserHelpHovered(true)
        },
        onMouseLeave: () => {
          setIsUserHelpHovered(false)
        },
      }

  useEffect(() => {
    onCountNotificationsSubscription.current = onCountNotificationsChange.subscribe((count: number) => {
      setCountNotifications(count)
    })
    return () => onCountNotificationsSubscription.current?.unsubscribe()
  }, [])

  useEffect(() => {
    onConfigChangeSubscription.current = onConfigChange.subscribe((configChanged: MySquid | any) => {
      if (configChanged?._id) {
        setConfig(configChanged)
      }
    })

    onUserChangeSubscription.current = onUserChange.subscribe(async (userChanged: User) => {
      if (userChanged?.squidId) {
        setUser(userChanged)
      }
    })

    return () => {
      onConfigChangeSubscription.current?.unsubscribe()
      onUserChangeSubscription.current?.unsubscribe()
    }
  }, [onConfigChangeSubscription, setUser, onHistorySubscription, onUserChangeSubscription])

  const getNotifications = useCallback(async () => {
    if (!loadingNotifications) {
      try {
        setLoadingNotification(true)
        const response = await alfredService.getCountNotifications()
        onCountNotificationsChange?.next(response?.sent || 0)
      } catch (error) {
        logService.error({ message: 'Error getting notifications', error })
      } finally {
        setLoadingNotification(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alfredService])

  useEffect(() => {
    getNotifications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  return (
    <header className={className}>
      <div className="container">
        <MainMenu open={openMobileMenu} setOpen={setOpenMobileMenu} mode="mobile" />
        <Link to="/" className="brand-logo" style={{ marginLeft: '0', height: '30px' }}>
          <Logo height={isSmallScreen ? '30px' : '40px'} />
        </Link>
        <div className="actions">
          <span
            className={`item user-notifications ${openNotifications ? 'active' : ''} ${isNotificationsHovered ? 'hover' : ''}`}
            {...(isMobile ? {} : hoverNotificationEvents)}
          >
            <button
              id="user-notifications-default-button"
              type="button"
              onClick={() => setOpenNotifications(!openNotifications)}
              ref={notificationsButton}
            >
              <i className={`fa-solid ${!openNotifications ? 'fa-bell' : 'fa-times'}`} />
              {countNotifications > 0 && !openNotifications ? (
                <span className="badge white">{countNotifications < 100 ? countNotifications : '99+'}</span>
              ) : null}
            </button>
            <Notifications open={openNotifications} setOpen={setOpenNotifications} button={notificationsButton} />
          </span>
          <NavLink
            className={({ isActive }) => {
              if (isUserHelpHovered || (isActive && !openNotifications)) {
                return 'active'
              }
              return ''
            }}
            {...(isMobile ? {} : hoverHelpEvents)}
            end
            to="/help"
          >
            <span className="item user-help">
              <i className="fa-solid fa-question"></i>
            </span>
          </NavLink>
          {!isSmallScreen && <LanguageDropdown />}
          <UserMenu configs={configs} user={user} />
        </div>
      </div>
    </header>
  )
}
